import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Image } from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import TextAlign from '@tiptap/extension-text-align';
import HardBreak from '@tiptap/extension-hard-break';
import { Node, mergeAttributes } from '@tiptap/core';
import { all, createLowlight } from 'lowlight';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Save, X, Bold, Italic, Code, List, ListOrdered, Heading1, Heading2, Heading3, Table as TableIcon, ImageIcon, Video, Link as LinkIcon, Plus, Tag, IndentIncrease, IndentDecrease, Undo, Redo, Strikethrough, Quote, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

const lowlight = createLowlight(all);

// Custom Iframe extension for embedding videos from various sources
const Iframe = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  
  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: '100%',
      },
      height: {
        default: 400,
      },
      frameborder: {
        default: 0,
      },
      allowfullscreen: {
        default: true,
      },
      allow: {
        default: 'autoplay; fullscreen; encrypted-media',
      },
    };
  },
  
  parseHTML() {
    return [{
      tag: 'iframe',
    }];
  },
  
  renderHTML({ HTMLAttributes }) {
    return ['div', { 
      class: 'video-embed',
      style: `max-width: ${HTMLAttributes.width || '100%'}; margin: 1rem 0;`
    }, ['iframe', mergeAttributes(HTMLAttributes, {
      style: `width: 100%; height: ${HTMLAttributes.height || 400}px; border-radius: 8px;`
    })]];
  },
  
  addCommands() {
    return {
      setIframe: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

// Custom Video extension for direct video files
const VideoEmbed = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  
  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: '100%',
      },
      height: {
        default: 400,
      },
      controls: {
        default: true,
      },
    };
  },
  
  parseHTML() {
    return [{
      tag: 'video',
    }];
  },
  
  renderHTML({ HTMLAttributes }) {
    return ['div', { 
      class: 'video-embed',
      style: `max-width: ${HTMLAttributes.width || '100%'}; margin: 1rem 0;`
    }, ['video', mergeAttributes(HTMLAttributes, { 
      controls: true,
      controlsList: 'nodownload noplaybackrate',
      disablePictureInPicture: true,
      onContextMenu: 'return false;',
      style: `width: 100%; height: ${HTMLAttributes.height || 400}px; border-radius: 8px;`
    })]];
  },
  
  addCommands() {
    return {
      setVideo: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

const MenuBar = ({ editor, onAddImage, onAddVideo, onAddLink }) => {
  if (!editor) return null;

  // Handle indent for both lists and regular text
  const handleIndent = () => {
    if (editor.isActive('listItem')) {
      editor.chain().focus().sinkListItem('listItem').run();
    } else {
      // For regular text, wrap in a div with margin or use blockquote
      editor.chain().focus().toggleBlockquote().run();
    }
  };

  // Handle outdent for both lists and regular text
  const handleOutdent = () => {
    if (editor.isActive('listItem')) {
      editor.chain().focus().liftListItem('listItem').run();
    } else if (editor.isActive('blockquote')) {
      editor.chain().focus().toggleBlockquote().run();
    }
  };

  return (
    <div className="sticky top-0 z-10 flex flex-wrap gap-1 p-3 border-b border-zinc-200 bg-zinc-50 shadow-sm">
      {/* Undo/Redo */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Ctrl+Z)"
        className="h-8 w-8 p-0"
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Ctrl+Y)"
        className="h-8 w-8 p-0"
      >
        <Redo className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-zinc-300 mx-1" />
      
      {/* Text Formatting */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bold') ? 'bg-zinc-200' : ''}`}
        title="Bold (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('italic') ? 'bg-zinc-200' : ''}`}
        title="Italic (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('strike') ? 'bg-zinc-200' : ''}`}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleCode().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('code') ? 'bg-zinc-200' : ''}`}
        title="Inline Code"
      >
        <Code className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-zinc-300 mx-1" />
      
      {/* Headings Dropdown */}
      <select
        className="h-8 px-2 text-sm border border-zinc-200 rounded bg-white"
        value={
          editor.isActive('heading', { level: 1 }) ? 'h1' :
          editor.isActive('heading', { level: 2 }) ? 'h2' :
          editor.isActive('heading', { level: 3 }) ? 'h3' :
          'p'
        }
        onChange={(e) => {
          const val = e.target.value;
          if (val === 'p') {
            editor.chain().focus().setParagraph().run();
          } else {
            const level = parseInt(val.replace('h', ''));
            editor.chain().focus().toggleHeading({ level }).run();
          }
        }}
      >
        <option value="p">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
      </select>
      <div className="w-px h-8 bg-zinc-300 mx-1" />
      
      {/* Text Alignment */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'left' }) ? 'bg-zinc-200' : ''}`}
        title="Align Left"
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'center' }) ? 'bg-zinc-200' : ''}`}
        title="Align Center"
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={`h-8 w-8 p-0 ${editor.isActive({ textAlign: 'right' }) ? 'bg-zinc-200' : ''}`}
        title="Align Right"
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-zinc-300 mx-1" />
      
      {/* Lists */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('bulletList') ? 'bg-zinc-200' : ''}`}
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('orderedList') ? 'bg-zinc-200' : ''}`}
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-zinc-300 mx-1" />
      
      {/* Indent/Outdent */}
      <Button
        size="sm"
        variant="ghost"
        onClick={handleIndent}
        className="h-8 w-8 p-0 hover:bg-violet-100"
        title="Indent - Increase indentation (Tab in lists, or add blockquote)"
      >
        <IndentIncrease className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleOutdent}
        className="h-8 w-8 p-0 hover:bg-violet-100"
        title="Outdent - Decrease indentation (Shift+Tab in lists, or remove blockquote)"
      >
        <IndentDecrease className="h-4 w-4" />
      </Button>
      <div className="w-px h-8 bg-zinc-300 mx-1" />
      
      {/* Blockquote */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`h-8 w-8 p-0 ${editor.isActive('blockquote') ? 'bg-zinc-200' : ''}`}
        title="Quote"
      >
        <Quote className="h-4 w-4" />
      </Button>
      
      {/* Code Block */}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`h-8 px-2 text-xs font-mono ${editor.isActive('codeBlock') ? 'bg-zinc-200' : ''}`}
        title="Code Block"
      >
        {'</>'}
      </Button>
      <div className="w-px h-8 bg-zinc-300 mx-1" />
      
      {/* Media */}
      <Button
        size="sm"
        variant="ghost"
        onClick={onAddImage}
        title="Insert Image (or paste from clipboard)"
        className="h-8 w-8 p-0"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onAddVideo}
        title="Insert Video (YouTube, Vimeo, Google Drive, Loom, or direct URL)"
        className="h-8 w-8 p-0"
      >
        <Video className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onAddLink}
        className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-zinc-200' : ''}`}
        title="Insert Link"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        title="Insert Table"
        className="h-8 w-8 p-0"
      >
        <TableIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Floating Toolbar Component - appears when text is selected
const FloatingToolbar = ({ editor, onAddLink }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const toolbarRef = useRef(null);

  useEffect(() => {
    if (!editor) return;

    const updateToolbar = () => {
      const { selection } = editor.state;
      const { from, to } = selection;
      
      // Only show when there's a text selection (not just a cursor)
      if (from === to || selection.empty) {
        setIsVisible(false);
        return;
      }

      // Get selection coordinates
      const coords = editor.view.coordsAtPos(from);
      const endCoords = editor.view.coordsAtPos(to);
      
      // Calculate position (centered above selection)
      const editorElement = editor.view.dom;
      const editorRect = editorElement.getBoundingClientRect();
      
      const left = Math.min(coords.left, endCoords.left) + (Math.abs(endCoords.left - coords.left) / 2) - editorRect.left;
      const top = Math.min(coords.top, endCoords.top) - editorRect.top - 45;

      setPosition({ top, left: Math.max(10, left) });
      setIsVisible(true);
    };

    editor.on('selectionUpdate', updateToolbar);
    editor.on('blur', () => setIsVisible(false));

    return () => {
      editor.off('selectionUpdate', updateToolbar);
      editor.off('blur', () => setIsVisible(false));
    };
  }, [editor]);

  if (!editor || !isVisible) return null;

  return (
    <div
      ref={toolbarRef}
      className="absolute z-50 flex gap-1 p-2 bg-zinc-800 rounded-lg shadow-lg transition-opacity duration-150"
      style={{
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none'
      }}
    >
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
        className={`h-7 w-7 p-0 text-white hover:bg-zinc-700 ${editor.isActive('bold') ? 'bg-zinc-600' : ''}`}
      >
        <Bold className="h-3 w-3" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
        className={`h-7 w-7 p-0 text-white hover:bg-zinc-700 ${editor.isActive('italic') ? 'bg-zinc-600' : ''}`}
      >
        <Italic className="h-3 w-3" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleStrike().run(); }}
        className={`h-7 w-7 p-0 text-white hover:bg-zinc-700 ${editor.isActive('strike') ? 'bg-zinc-600' : ''}`}
      >
        <Strikethrough className="h-3 w-3" />
      </Button>
      <div className="w-px h-7 bg-zinc-600 mx-1" />
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 1 }).run(); }}
        className={`h-7 px-2 text-white hover:bg-zinc-700 text-xs font-bold ${editor.isActive('heading', { level: 1 }) ? 'bg-zinc-600' : ''}`}
      >
        H1
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run(); }}
        className={`h-7 px-2 text-white hover:bg-zinc-700 text-xs font-bold ${editor.isActive('heading', { level: 2 }) ? 'bg-zinc-600' : ''}`}
      >
        H2
      </Button>
      <div className="w-px h-7 bg-zinc-600 mx-1" />
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); onAddLink(); }}
        className={`h-7 w-7 p-0 text-white hover:bg-zinc-700 ${editor.isActive('link') ? 'bg-zinc-600' : ''}`}
      >
        <LinkIcon className="h-3 w-3" />
      </Button>
      <div className="w-px h-7 bg-zinc-600 mx-1" />
      {/* Bullet and Numbered Lists */}
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
        className={`h-7 w-7 p-0 text-white hover:bg-zinc-700 ${editor.isActive('bulletList') ? 'bg-zinc-600' : ''}`}
        title="Bullet List"
      >
        <List className="h-3 w-3" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
        className={`h-7 w-7 p-0 text-white hover:bg-zinc-700 ${editor.isActive('orderedList') ? 'bg-zinc-600' : ''}`}
        title="Numbered List"
      >
        <ListOrdered className="h-3 w-3" />
      </Button>
      <div className="w-px h-7 bg-zinc-600 mx-1" />
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { 
          e.preventDefault(); 
          if (editor.isActive('listItem')) {
            editor.chain().focus().sinkListItem('listItem').run();
          } else {
            editor.chain().focus().toggleBlockquote().run();
          }
        }}
        className="h-7 w-7 p-0 text-white hover:bg-zinc-700"
        title="Indent"
      >
        <IndentIncrease className="h-3 w-3" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onMouseDown={(e) => { 
          e.preventDefault(); 
          if (editor.isActive('listItem')) {
            editor.chain().focus().liftListItem('listItem').run();
          } else if (editor.isActive('blockquote')) {
            editor.chain().focus().toggleBlockquote().run();
          }
        }}
        className="h-7 w-7 p-0 text-white hover:bg-zinc-700"
        title="Outdent"
      >
        <IndentDecrease className="h-3 w-3" />
      </Button>
    </div>
  );
};

const DocumentEditor = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState(location.state?.category || '');
  const [tags, setTags] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoSize, setVideoSize] = useState('medium'); // small, medium, large, custom
  const [customWidth, setCustomWidth] = useState('640');
  const [customHeight, setCustomHeight] = useState('360');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        heading: {
          levels: [1, 2, 3],
        },
        hardBreak: false, // Disable default, we'll use our own
      }),
      HardBreak.configure({
        keepMarks: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Image.configure({
        allowBase64: true,
        inline: true,
      }),
      Youtube.configure({
        width: 640,
        height: 360,
      }),
      Iframe,
      VideoEmbed,
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder: 'Start writing your document... (Tip: You can paste images directly!)',
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-zinc max-w-none focus:outline-none min-h-[400px] p-6 prose-headings:font-bold prose-h1:text-3xl prose-h1:mb-4 prose-h1:mt-6 prose-h2:text-2xl prose-h2:mb-3 prose-h2:mt-5 prose-h3:text-xl prose-h3:mb-2 prose-h3:mt-4 prose-p:my-3 prose-blockquote:border-l-4 prose-blockquote:border-violet-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:bg-zinc-50 prose-blockquote:py-2',
      },
      // Preserve empty paragraphs by converting to &nbsp;
      transformPastedHTML(html) {
        // Replace empty paragraphs with paragraphs containing &nbsp;
        return html.replace(/<p><\/p>/g, '<p>&nbsp;</p>')
                   .replace(/<p>\s*<\/p>/g, '<p>&nbsp;</p>');
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        const html = event.clipboardData?.getData('text/html');
        const text = event.clipboardData?.getData('text/plain');
        
        // Handle image paste first
        if (items) {
          for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
              event.preventDefault();
              const file = items[i].getAsFile();
              if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                  const base64 = e.target?.result;
                  if (base64 && editor) {
                    editor.chain().focus().setImage({ src: base64 }).run();
                    toast.success('Image pasted successfully!');
                  }
                };
                reader.readAsDataURL(file);
              }
              return true;
            }
          }
        }
        
        // Handle HTML paste with better formatting preservation
        if (html) {
          event.preventDefault();
          
          // Clean and transform HTML
          const cleanedHtml = cleanPastedHtml(html);
          
          if (editor) {
            editor.chain().focus().insertContent(cleanedHtml).run();
          }
          return true;
        }
        
        // Handle plain text with line breaks
        if (text && !html) {
          event.preventDefault();
          // Convert line breaks to paragraphs
          const paragraphs = text.split(/\n\n+/).map(p => {
            const lines = p.split(/\n/).join('<br>');
            return `<p>${lines || '&nbsp;'}</p>`;
          }).join('');
          
          if (editor) {
            editor.chain().focus().insertContent(paragraphs).run();
          }
          return true;
        }
        
        return false;
      },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          for (let i = 0; i < files.length; i++) {
            if (files[i].type.indexOf('image') !== -1) {
              event.preventDefault();
              const reader = new FileReader();
              reader.onload = (e) => {
                const base64 = e.target?.result;
                if (base64 && editor) {
                  editor.chain().focus().setImage({ src: base64 }).run();
                  toast.success('Image dropped successfully!');
                }
              };
              reader.readAsDataURL(files[i]);
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  // Clean pasted HTML to preserve formatting but remove unwanted styles
  const cleanPastedHtml = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Remove all style attributes except essential ones
    const allElements = doc.querySelectorAll('*');
    allElements.forEach(el => {
      // Remove inline styles that might break layout
      el.removeAttribute('style');
      el.removeAttribute('class');
      
      // Convert font tags to spans with proper formatting
      if (el.tagName === 'FONT') {
        const span = doc.createElement('span');
        span.innerHTML = el.innerHTML;
        el.parentNode?.replaceChild(span, el);
      }
    });
    
    // Preserve empty paragraphs by adding &nbsp;
    const emptyParagraphs = doc.querySelectorAll('p:empty');
    emptyParagraphs.forEach(p => {
      p.innerHTML = '&nbsp;';
    });
    
    // Also handle paragraphs with only whitespace
    const paragraphs = doc.querySelectorAll('p');
    paragraphs.forEach(p => {
      if (p.textContent?.trim() === '' && !p.querySelector('img, br')) {
        p.innerHTML = '&nbsp;';
      }
    });
    
    // Convert MS Word specific elements
    const wordParagraphs = doc.querySelectorAll('p.MsoNormal, p.MsoListParagraph');
    wordParagraphs.forEach(p => {
      p.removeAttribute('class');
    });
    
    // Preserve heading structure
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach(h => {
      h.removeAttribute('class');
      h.removeAttribute('style');
    });
    
    return doc.body.innerHTML;
  };

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchDocument();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchDocument = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/documents/${id}`);
      setTitle(res.data.title);
      setCategory(res.data.category || '');
      setTags(res.data.tags?.join(', ') || '');
      if (editor && res.data.content) {
        editor.commands.setContent(res.data.content);
      }
    } catch (error) {
      toast.error('Failed to load document');
      navigate('/documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editor && id) {
      // Re-set content when editor is ready
      const fetchAndSet = async () => {
        try {
          const res = await axios.get(`${API_URL}/documents/${id}`);
          if (res.data.content) {
            editor.commands.setContent(res.data.content);
          }
        } catch (error) {
          console.error('Error setting content:', error);
        }
      };
      fetchAndSet();
    }
  }, [editor, id]);

  const handleAddImage = () => {
    setImageUrl('');
    setImageDialogOpen(true);
  };

  const handleInsertImage = () => {
    if (imageUrl && editor) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageDialogOpen(false);
      setImageUrl('');
    }
  };

  const handleAddVideo = () => {
    setVideoUrl('');
    setVideoSize('medium');
    setCustomWidth('640');
    setCustomHeight('360');
    setVideoDialogOpen(true);
  };

  const handleInsertVideo = () => {
    if (videoUrl && editor) {
      const url = videoUrl.trim();
      
      // Determine video dimensions based on size selection
      let width, height;
      switch (videoSize) {
        case 'small':
          width = '480px';
          height = '270px';
          break;
        case 'medium':
          width = '640px';
          height = '360px';
          break;
        case 'large':
          width = '854px';
          height = '480px';
          break;
        case 'full':
          width = '100%';
          height = '500px';
          break;
        case 'custom':
          width = `${customWidth}px`;
          height = `${customHeight}px`;
          break;
        default:
          width = '640px';
          height = '360px';
      }
      
      // YouTube - use built-in extension
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        // Add params to disable related videos and sharing
        let ytUrl = url;
        const sep = ytUrl.includes('?') ? '&' : '?';
        ytUrl += `${sep}rel=0&modestbranding=1`;
        editor.chain().focus().setYoutubeVideo({ 
          src: ytUrl,
          width: parseInt(width.replace('px', '')) || 640,
          height: parseInt(height.replace('px', '')) || 360
        }).run();
        toast.success('YouTube video added!');
      }
      // Google Drive
      else if (url.includes('drive.google.com')) {
        // Extract file ID from various Google Drive URL formats
        let fileId = null;
        
        // Format: /d/FILE_ID/
        const dMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
        if (dMatch) fileId = dMatch[1];
        
        // Format: id=FILE_ID
        if (!fileId) {
          const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
          if (idMatch) fileId = idMatch[1];
        }
        
        // Format: /file/d/FILE_ID
        if (!fileId) {
          const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
          if (fileMatch) fileId = fileMatch[1];
        }
        
        if (fileId) {
          const embedUrl = `https://drive.google.com/file/d/${fileId}/preview`;
          editor.chain().focus().setIframe({ 
            src: embedUrl,
            width: width,
            height: parseInt(height.replace('px', '')) || 400
          }).run();
          toast.success('Google Drive video added!');
        } else {
          toast.error('Could not extract file ID from Google Drive URL');
          return;
        }
      }
      // Vimeo
      else if (url.includes('vimeo.com')) {
        const vimeoId = url.match(/vimeo\.com\/(\d+)/)?.[1];
        if (vimeoId) {
          editor.chain().focus().setIframe({ 
            src: `https://player.vimeo.com/video/${vimeoId}`,
            width: width,
            height: parseInt(height.replace('px', '')) || 400
          }).run();
          toast.success('Vimeo video added!');
        } else {
          toast.error('Could not extract video ID from Vimeo URL');
          return;
        }
      }
      // Loom
      else if (url.includes('loom.com')) {
        const loomId = url.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/)?.[1];
        if (loomId) {
          editor.chain().focus().setIframe({ 
            src: `https://www.loom.com/embed/${loomId}`,
            width: width,
            height: parseInt(height.replace('px', '')) || 400
          }).run();
          toast.success('Loom video added!');
        } else {
          toast.error('Could not extract video ID from Loom URL');
          return;
        }
      }
      // Direct video URL (MP4, WebM, etc.)
      else if (url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i)) {
        editor.chain().focus().setVideo({ 
          src: url,
          width: width,
          height: parseInt(height.replace('px', '')) || 400
        }).run();
        toast.success('Video file added!');
      }
      // Generic URL - try as iframe
      else {
        editor.chain().focus().setIframe({ 
          src: url,
          width: width,
          height: parseInt(height.replace('px', '')) || 400
        }).run();
        toast.success('Video embedded!');
      }
      
      setVideoDialogOpen(false);
      setVideoUrl('');
      setVideoSize('medium');
      setCustomWidth('640');
      setCustomHeight('360');
    }
  };

  const handleAddLink = () => {
    const selection = editor?.state.selection;
    if (selection && !selection.empty) {
      const text = editor?.state.doc.textBetween(selection.from, selection.to);
      setLinkText(text || '');
    } else {
      setLinkText('');
    }
    setLinkUrl('');
    setLinkDialogOpen(true);
  };

  const handleInsertLink = () => {
    if (linkUrl && editor) {
      if (editor.state.selection.empty && linkText) {
        editor.chain().focus().insertContent(`<a href="${linkUrl}">${linkText}</a>`).run();
      } else {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
      setLinkDialogOpen(false);
      setLinkUrl('');
      setLinkText('');
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Please enter a document title');
      return;
    }

    if (!editor) return;

    setSaving(true);
    try {
      const content = editor.getHTML();
      const tagsArray = tags.split(',').map(t => t.trim()).filter(Boolean);
      
      const data = {
        title,
        content,
        category: category || null,
        tags: tagsArray
      };

      if (id) {
        await axios.put(`${API_URL}/documents/${id}`, data);
        toast.success('Document updated successfully!');
      } else {
        await axios.post(`${API_URL}/documents`, data);
        toast.success('Document created successfully!');
      }
      navigate('/documents');
    } catch (error) {
      toast.error('Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  // Build category tree for dropdown
  const buildCategoryOptions = (cats, level = 0) => {
    return cats.map(cat => {
      const indent = '  '.repeat(level);
      const prefix = level > 0 ? '└─ ' : '';
      return (
        <option key={cat.id} value={cat.name}>
          {indent}{prefix}{cat.name}
        </option>
      );
    });
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="document-editor">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {id ? 'Edit Document' : 'New Document'}
          </h1>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/documents')}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          {/* Document Info */}
          <div className="p-6 border-b border-zinc-200 space-y-4">
            <div>
              <Label className="text-base font-semibold">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter document title..."
                className="mt-2 text-lg"
                data-testid="document-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full h-10 px-3 mt-2 rounded-md border border-zinc-200 bg-white text-sm"
                  data-testid="document-category-select"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.name}>
                      {'  '.repeat(cat.level || 0)}{cat.level > 0 ? '└─ ' : ''}{cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags (comma-separated)
                </Label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="e.g. api, guide, tutorial"
                  className="mt-2"
                  data-testid="document-tags-input"
                />
              </div>
            </div>
          </div>

          {/* Tip Box */}
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
            <p className="text-sm text-blue-700">
              💡 <strong>Tip:</strong> You can paste images directly (Ctrl+V) or drag & drop them into the editor!
            </p>
          </div>

          {/* Editor */}
          <MenuBar 
            editor={editor} 
            onAddImage={handleAddImage}
            onAddVideo={handleAddVideo}
            onAddLink={handleAddLink}
          />
          
          {/* Editor Content with Floating Toolbar */}
          <div className="relative">
            <FloatingToolbar editor={editor} onAddLink={handleAddLink} />
            <EditorContent editor={editor} className="min-h-[500px]" />
          </div>
        </div>

        {/* Image Dialog */}
        <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Insert Image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Image URL</Label>
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.png"
                  className="mt-2"
                />
              </div>
              <p className="text-sm text-zinc-500">
                Or paste an image directly into the editor (Ctrl+V)
              </p>
              <Button onClick={handleInsertImage} className="w-full bg-violet-600 hover:bg-violet-700">
                Insert Image
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Video Dialog */}
        <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Insert Video</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Video URL</Label>
                <Input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="Paste video URL here..."
                  className="mt-2"
                />
              </div>
              
              {/* Video Size Options */}
              <div>
                <Label className="text-sm font-medium">Video Size</Label>
                <select
                  value={videoSize}
                  onChange={(e) => setVideoSize(e.target.value)}
                  className="w-full h-10 px-3 mt-2 rounded-md border border-zinc-200 bg-white text-sm"
                >
                  <option value="small">Small (480×270)</option>
                  <option value="medium">Medium (640×360)</option>
                  <option value="large">Large (854×480)</option>
                  <option value="full">Full Width (100%×500)</option>
                  <option value="custom">Custom Size</option>
                </select>
              </div>
              
              {/* Custom Size Inputs */}
              {videoSize === 'custom' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Width (px)</Label>
                    <Input
                      type="number"
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      placeholder="640"
                      className="mt-1"
                      min="200"
                      max="1200"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Height (px)</Label>
                    <Input
                      type="number"
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      placeholder="360"
                      className="mt-1"
                      min="150"
                      max="800"
                    />
                  </div>
                </div>
              )}
              
              <div className="text-sm text-zinc-500 space-y-1">
                <p className="font-medium text-zinc-700">Supported sources:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>YouTube (youtube.com, youtu.be)</li>
                  <li>Google Drive (drive.google.com)</li>
                  <li>Vimeo (vimeo.com)</li>
                  <li>Loom (loom.com)</li>
                  <li>Direct video files (.mp4, .webm, .ogg)</li>
                </ul>
              </div>
              <Button onClick={handleInsertVideo} className="w-full bg-violet-600 hover:bg-violet-700">
                Insert Video
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Link Dialog */}
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Insert Link</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>URL</Label>
                <Input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="mt-2"
                />
              </div>
              <div>
                <Label>Link Text (optional)</Label>
                <Input
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Click here"
                  className="mt-2"
                />
              </div>
              <Button onClick={handleInsertLink} className="w-full bg-violet-600 hover:bg-violet-700">
                Insert Link
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DocumentEditor;
